import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  ViewChild,
  effect,
  inject,
  runInInjectionContext,
} from '@angular/core';
import { MapListing } from '../../models/map-listing.model';
import { StartPoint } from '../../models/start-point.model';
import { track } from '../../services/analytics';
import { loadGmapsLibraries } from '../../services/gmaps-loader';
import { ListingDetailService } from '../../services/listing-detail.service';
import { ListingsService } from '../../services/listings.service';
import { MapFiltersService } from '../../services/map-filters.service';
import { PlannerStore } from '../../services/planner-store';
import { buildStartPin } from './category-pin';
import { MapGestureTracker } from './map-gestures';
import { MAP_FIT_PADDING, MarkerLayer } from './marker-layer';

// Serbia's bounding box. The map fits to this on load so the first screen always
// shows the whole country at any viewport - a fixed zoom cannot do that (zoom 8
// covers ~2 degrees of longitude on a 375px phone; Serbia spans ~4).
const SERBIA_BOUNDS = { south: 41.85, west: 18.82, north: 46.19, east: 23.01 };

// The Map constructor requires center+zoom; fitBounds overrides both a frame
// later. Centroid of SERBIA_BOUNDS, so even that first frame is centred.
const INITIAL_CENTER = { lat: 44.0, lng: 20.9 };
const INITIAL_ZOOM = 7;

const DIRECTIONS_DEBOUNCE_MS = 250;

// Ceiling zoom for a ?regija= region fit, so a region with one/few geocoded
// listings does not slam fitBounds to street level. Below CLUSTER_MAX_ZOOM (14)
// so the framed region stays legible; released on the first idle so any later
// zoom the visitor does is uncapped.
const REGION_FIT_MAX_ZOOM = 12;

// What sat under the pointer at the first touch: which class of marker, or 'map'
// for the bare surface. Markers are plain DOM nodes inside the map div
// (AdvancedMarkerElement content), so the class the pin builders set is the whole
// test. Read synchronously off the event that already fires planner_map_interact,
// so reporting it changes neither the event's count nor its timing - which is why
// the pre-existing 'kind' series stays comparable across this change.
function pinTargetAt(target: EventTarget | null): string {
  const pin = target instanceof Element ? target.closest('.djir-pin') : null;
  if (!pin) return 'map';
  if (pin.classList.contains('djir-pin-cluster')) return 'cluster';
  if (pin.classList.contains('djir-pin-group')) return 'group';
  if (pin.classList.contains('djir-pin-start')) return 'start';
  return 'pin';
}

// A point the route passes through: the optional start point, then each stop.
interface Waypoint {
  lat: number;
  lng: number;
}

@Component({
  selector: 'app-map-view',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './map-view.component.html',
  styleUrl: './map-view.component.css',
})
export class MapViewComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapEl', { static: true }) mapEl!: ElementRef<HTMLDivElement>;

  private readonly listingsService = inject(ListingsService);
  private readonly detailService = inject(ListingDetailService);
  private readonly store = inject(PlannerStore);
  private readonly injector = inject(Injector);
  readonly filters = inject(MapFiltersService);

  private markerLayer: MarkerLayer | null = null;
  private map: google.maps.Map | null = null;
  private startMarker: google.maps.marker.AdvancedMarkerElement | null = null;
  private routeClass: typeof google.maps.routes.Route | null = null;
  private routePolylines: google.maps.Polyline[] = [];
  private routeDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private markerLib: google.maps.MarkerLibrary | null = null;
  // Set when the first tiles paint; used as the origin for ms_since_ready.
  private mapReadyAt = 0;
  // Removes the first-interaction listeners; null once they have been removed.
  private interactCleanup: (() => void) | null = null;
  private gestures: MapGestureTracker | null = null;

  errorMessage: string | null = null;

  async ngAfterViewInit(): Promise<void> {
    try {
      const [{ maps, marker, geometry }] = await Promise.all([
        loadGmapsLibraries(),
        this.listingsService.fetchMapListings(),
      ]);

      const serviceError = this.listingsService.error();
      if (serviceError) throw new Error(serviceError);

      this.markerLib = marker;

      this.map = new maps.Map(this.mapEl.nativeElement, {
        center: INITIAL_CENTER,
        zoom: INITIAL_ZOOM,
        mapId: '3d1240517321afe5644f29a6', // "djir-planner-map" in Cloud console; this is its Map ID, not the display name
        disableDefaultUI: false,
        clickableIcons: false,
        streetViewControl: false,
        fullscreenControl: false,
        gestureHandling: 'greedy',
      });

      // Constructed before the opening fit so that fit is already suppressed:
      // every map move from here on is either announced as ours or counted as
      // the visitor's.
      this.gestures = new MapGestureTracker(this.map);

      // Open on the region (?regija=<slug>) when deep-linked, else the whole
      // country. A saved trip re-fits to its route later, from the debounced
      // directions effect, so this opening fit only sticks for visitors who
      // arrive without stops - which is every visitor coming from an ad.
      this.gestures.suppress();
      const regionBounds = this.store.regionBounds();
      if (regionBounds) {
        // Frame the region so its pins are individually tappable, not one
        // clustered blob at country zoom. Same MAP_FIT_PADDING as the Serbia fit,
        // so edge pins clear the fixed header (top) and the plan-sheet peek
        // (bottom). maxZoom guards a tiny region from jumping to street level;
        // released on idle so the visitor's later zoom is uncapped.
        this.map.setOptions({ maxZoom: REGION_FIT_MAX_ZOOM });
        this.map.fitBounds(
          new google.maps.LatLngBounds(
            { lat: regionBounds.south, lng: regionBounds.west },
            { lat: regionBounds.north, lng: regionBounds.east }
          ),
          MAP_FIT_PADDING
        );
        google.maps.event.addListenerOnce(this.map, 'idle', () => {
          this.map?.setOptions({ maxZoom: null });
        });
      } else {
        this.map.fitBounds(
          new google.maps.LatLngBounds(
            { lat: SERBIA_BOUNDS.south, lng: SERBIA_BOUNDS.west },
            { lat: SERBIA_BOUNDS.north, lng: SERBIA_BOUNDS.east }
          ),
          MAP_FIT_PADDING
        );
      }

      // 'tilesloaded' means tiles actually painted, so this is the moment the
      // visitor saw the opening view. ('idle' fires once movement settles, which
      // is a different question.) Fires once - reported against
      // planner_session_start it gives the share who left before seeing anything.
      google.maps.event.addListenerOnce(this.map, 'tilesloaded', () => {
        this.mapReadyAt = performance.now();
        track('planner_map_ready', {
          // performance.now()'s origin is navigation start, so this is time from
          // opening the page to a painted map.
          ms: Math.round(this.mapReadyAt),
          listings: this.listingsService.listings().length,
        });
      });

      this.trackFirstInteraction();

      const routesLib = (await google.maps.importLibrary(
        'routes'
      )) as google.maps.RoutesLibrary;
      this.routeClass = routesLib.Route;

      this.markerLayer = new MarkerLayer(this.map, marker, geometry, {
        onListingClick: (id) => this.detailService.open(id),
        onProgrammaticMove: () => this.gestures?.suppress(),
      });

      runInInjectionContext(this.injector, () => {
        // Rebuild markers whenever the listing set changes (initial load and
        // every filter re-fetch). Runs once immediately, so the first render is
        // identical to before.
        effect(() => {
          const listings = this.listingsService.listings();
          this.markerLayer!.setListings(listings, new Set(this.store.stops()));
        });

        effect(() => {
          const stopIds = new Set(this.store.stops());
          this.markerLayer!.updateBadges(stopIds);
        });

        effect(() => {
          const stops = this.store.stopListings();
          const start = this.store.startPoint();
          // Captured here, not at response time: the summary must carry the key
          // of the waypoints it was actually computed for.
          const key = this.store.waypointsKey();
          this.scheduleDirectionsUpdate(this.buildWaypoints(start, stops), key);
        });

        effect(() => {
          this.updateStartMarker(this.store.startPoint());
        });
      });
    } catch (e) {
      this.errorMessage = e instanceof Error ? e.message : String(e);
      // Counterpart to planner_map_ready: without it a missing ready event is
      // ambiguous between "the map failed" and "the visitor left before it drew".
      track('planner_map_error', { message: this.errorMessage });
      console.error('[map-view] init failed', e);
    }
  }

  // Reports the first genuine touch of the map surface, once per planner session.
  // Deliberately DOM-level rather than the map's own 'zoom_changed'/'bounds_changed':
  // those also fire for programmatic moves, and we make two of those (the Serbia
  // fit above and the route fit), so every load would report a phantom
  // interaction. Pointer and wheel events only ever come from the user.
  private trackFirstInteraction(): void {
    const el = this.mapEl.nativeElement;
    const fire = (kind: 'pointer' | 'wheel') => (e: Event) => {
      track('planner_map_interact', {
        kind,
        target: pinTargetAt(e.target),
        ms_since_ready: this.mapReadyAt
          ? Math.round(performance.now() - this.mapReadyAt)
          : null,
      });
      this.interactCleanup?.();
    };
    const onPointer = fire('pointer');
    const onWheel = fire('wheel');

    el.addEventListener('pointerdown', onPointer, { passive: true });
    el.addEventListener('wheel', onWheel, { passive: true });
    this.interactCleanup = () => {
      el.removeEventListener('pointerdown', onPointer);
      el.removeEventListener('wheel', onWheel);
      this.interactCleanup = null;
    };
  }

  ngOnDestroy(): void {
    // No-op once the interaction already fired and removed itself.
    this.interactCleanup?.();
    this.gestures?.destroy();
    this.markerLayer?.destroy();
    this.clearRoutePolylines();
    if (this.startMarker) {
      this.startMarker.map = null;
      this.startMarker = null;
    }
    if (this.routeDebounceHandle != null) {
      clearTimeout(this.routeDebounceHandle);
      this.routeDebounceHandle = null;
    }
  }

  private clearRoutePolylines(): void {
    for (const p of this.routePolylines) p.setMap(null);
    this.routePolylines = [];
  }

  // Ordered points the route runs through: the optional start point first, then
  // each plan stop. This is what drives the polyline and the route summary.
  private buildWaypoints(start: StartPoint | null, stops: MapListing[]): Waypoint[] {
    const points: Waypoint[] = [];
    if (start) points.push({ lat: start.lat, lng: start.lng });
    for (const s of stops) points.push({ lat: s.lat, lng: s.lng });
    return points;
  }

  // Creates/moves/removes the dedicated start-point marker. Managed here rather
  // than in MarkerLayer so it survives listing re-fetches and stays out of the
  // clusterer.
  private updateStartMarker(start: StartPoint | null): void {
    if (!this.map || !this.markerLib) return;

    if (!start) {
      if (this.startMarker) {
        this.startMarker.map = null;
        this.startMarker = null;
      }
      return;
    }

    const position = { lat: start.lat, lng: start.lng };
    if (this.startMarker) {
      this.startMarker.position = position;
      this.startMarker.title = start.name;
    } else {
      this.startMarker = new this.markerLib.AdvancedMarkerElement({
        position,
        title: start.name,
        content: buildStartPin(),
        gmpClickable: false,
        zIndex: 1000,
      });
      this.startMarker.map = this.map;
    }
  }

  private scheduleDirectionsUpdate(waypoints: Waypoint[], waypointsKey: string): void {
    if (this.routeDebounceHandle != null) {
      clearTimeout(this.routeDebounceHandle);
    }
    this.routeDebounceHandle = setTimeout(() => {
      this.routeDebounceHandle = null;
      void this.updateDirections(waypoints, waypointsKey);
    }, DIRECTIONS_DEBOUNCE_MS);
  }

  private async updateDirections(waypoints: Waypoint[], waypointsKey: string): Promise<void> {
    if (!this.routeClass || !this.map) return;

    this.clearRoutePolylines();

    if (waypoints.length < 2) {
      this.store.setRouteSummary(null);
      if (waypoints.length === 1) {
        this.map.panTo({ lat: waypoints[0].lat, lng: waypoints[0].lng });
      }
      return;
    }

    const origin = { location: { lat: waypoints[0].lat, lng: waypoints[0].lng } };
    const destination = {
      location: {
        lat: waypoints[waypoints.length - 1].lat,
        lng: waypoints[waypoints.length - 1].lng,
      },
    };
    const intermediates = waypoints.slice(1, -1).map((w) => ({
      location: { lat: w.lat, lng: w.lng },
    }));

    try {
      const { routes } = await this.routeClass.computeRoutes({
        origin,
        destination,
        intermediates,
        travelMode: google.maps.TravelMode.DRIVING,
        region: 'RS',
        language: 'sr-Latn',
        fields: ['distanceMeters', 'durationMillis', 'legs', 'path', 'viewport'],
      });

      const route = routes?.[0];
      if (!route) {
        this.store.setRouteSummary(null);
        return;
      }

      const polylines = route.createPolylines({
        polylineOptions: {
          strokeColor: '#2e7d32',
          strokeWeight: 4,
          strokeOpacity: 0.85,
        },
      });
      for (const p of polylines) p.setMap(this.map);
      this.routePolylines = polylines;

      const legs = route.legs ?? [];
      this.store.setRouteSummary({
        distanceMeters: route.distanceMeters ?? 0,
        durationSeconds: Math.round((route.durationMillis ?? 0) / 1000),
        legs: legs.map((l) => ({
          distanceMeters: l.distanceMeters ?? 0,
          durationSeconds: Math.round((l.durationMillis ?? 0) / 1000),
        })),
        waypointsKey,
      });

      const viewport = route.viewport;
      this.gestures?.suppress();
      if (viewport) {
        this.map.fitBounds(viewport, MAP_FIT_PADDING);
      } else {
        const bounds = new google.maps.LatLngBounds();
        for (const w of waypoints) bounds.extend({ lat: w.lat, lng: w.lng });
        this.map.fitBounds(bounds, MAP_FIT_PADDING);
      }
    } catch (e) {
      console.error('[map-view] computeRoutes failed', e);
      this.store.setRouteSummary(null);
    }
  }
}
